import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, FormControlLabel, Grid, MenuItem,
  Paper, Radio, RadioGroup, Snackbar, Stack, Tab, Tabs, TextField, Typography
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import GradingIcon from "@mui/icons-material/Grading";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { API_URL } from "./config";

const parseJsonArray = (v) => {
  if (Array.isArray(v)) return v;
  if (!v) return [];
  try { const x = JSON.parse(v);
    return Array.isArray(x) ? x : [];
  } catch { return String(v).split(",").map(x=>x.trim()).filter(Boolean);
  }
};

export default function FormStudentAssignmentSubmission() {
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const BLUE = "#1565C0";

  const token = localStorage.getItem("auth") || localStorage.getItem("token");
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [tasks, setTasks] = useState([]);
  const [mainTab, setMainTab] = useState(0);
  // 0 pending, 1 submitted, 2 graded
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [answers, setAnswers] = useState({});
  const [file, setFile] = useState(null);
  const [subjectCodeFilter, setSubjectCodeFilter] = useState("");
  const [subjectNameCodeFilter, setSubjectNameCodeFilter] = useState("");
  const [assignmentGroupFilter, setAssignmentGroupFilter] = useState("");
  const [titleFilter, setTitleFilter] = useState("");
  const [taskTypeFilter, setTaskTypeFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: "success", text: "" });

  const pageWrapSx = { p: 2, backgroundColor: BG, minHeight: "100vh" };
  const rootPaperSx = { mx: "auto", borderRadius: 2, overflow: "hidden", border: "1px solid rgba(25,118,210,0.15)" };
  const headerSx = { px: 2, py: 1.5, background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`, color: "white" };
  const surfaceSx = { borderRadius: 2, backgroundColor: "white", border: `1px solid ${BORDER}` };
  const inputSx = { "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: BORDER }, "&:hover fieldset": { borderColor: BORDER_HOVER }, "&.Mui-focused fieldset": { borderColor: BORDER_FOCUS } } };
  const primaryBtnSx = { borderRadius: 2, textTransform: "none", fontWeight: 900, backgroundColor: THEME_ORANGE, "&:hover": { backgroundColor: THEME_ORANGE_2 } };

  const loadTasks = async () => {
    const r = await axios.get(`${API_URL}/api/studentassignments/student/tasks`, { headers: authHeaders });
    setTasks(r.data?.tasks || []);
  };
  useEffect(() => { if (token) loadTasks().catch(()=>setMsg({type:"error",text:"Failed to load assignments"})); }, [token]);
  // eslint-disable-line

  const subjectCodeOptions = useMemo(() => [...new Set(tasks.flatMap(t => parseJsonArray(t.SubjectCodeList || t.SubjectCodes)))].sort(), [tasks]);
  const subjectNameCodeOptions = useMemo(() => [...new Set(tasks.flatMap(t => parseJsonArray(t.SubjectNameCodeList || t.SubjectCodes)))].sort(), [tasks]);
  const assignmentGroupOptions = useMemo(() => [...new Set(tasks.map(t => t.AssignmentGroupCode).filter(Boolean))].sort(), [tasks]);
  const tabRows = useMemo(() => tasks.filter(t => {
    const status = t.SubmissionStatus || "NOT_SUBMITTED";
    if (mainTab === 0 && status !== "NOT_SUBMITTED") return false;
    if (mainTab === 1 && status !== "SUBMITTED") return false;
    if (mainTab === 2 && status !== "GRADED") return false;
    if (subjectCodeFilter && !parseJsonArray(t.SubjectCodeList || t.SubjectCodes).includes(subjectCodeFilter)) return false;
    if (subjectNameCodeFilter && !parseJsonArray(t.SubjectNameCodeList || t.SubjectCodes).includes(subjectNameCodeFilter)) return false;
    if (assignmentGroupFilter && t.AssignmentGroupCode !== assignmentGroupFilter) return false;
    if (titleFilter.trim() && !String(t.Title || "").toLowerCase().includes(titleFilter.trim().toLowerCase())) return false;
    if (taskTypeFilter && t.TaskType !== taskTypeFilter) return false;
    return true;
  }), [tasks, mainTab, subjectCodeFilter, subjectNameCodeFilter, assignmentGroupFilter, titleFilter, taskTypeFilter]);

  const openTask = async (task) => {
    setSelected(task);
    setLoading(true);
    setAnswers({});
    setFile(null);
    try {
      const r = await axios.get(`${API_URL}/api/studentassignments/student/task/${task.AssignmentID || task.id}`, { headers: authHeaders });
      setDetail(r.data);
      const saved = {};
      (r.data?.submittedAnswers || []).forEach(a => {
        saved[a.QuestionID] = { answerText: a.AnswerText || "", selectedOptionIndex: a.SelectedOptionIndex, AwardedPoints: a.AwardedPoints, GraderFeedback: a.GraderFeedback, IsCorrect: a.IsCorrect, AutoScore: a.AutoScore };
      });
      setAnswers(saved);
    } catch (e) { setMsg({ type:"error", text:e.response?.data?.error || "Failed to open task" });
    }
    finally { setLoading(false);
    }
  };

  const submit = async () => {
    if (!detail?.canSubmit) return setMsg({ type:"error", text:"This task has already been submitted. Resubmission is not allowed." });
    const missing = detail.questions.find(q => q.QuestionType === "MCQ" ? answers[q.id]?.selectedOptionIndex == null : !String(answers[q.id]?.answerText || "").trim());
    if (missing) return setMsg({ type:"error", text:"Please answer every question before submitting" });
    const fd = new FormData();
    fd.append("answers", JSON.stringify(detail.questions.map(q => ({ questionID:q.id, answerText:answers[q.id]?.answerText || "", selectedOptionIndex:answers[q.id]?.selectedOptionIndex }))));
    if (file) fd.append("file", file);
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/studentassignments/student/task/${detail.task.AssignmentID}`, fd, { headers:{...authHeaders,"Content-Type":"multipart/form-data"} });
      setMsg({ type:"success", text:`Submitted successfully. MCQ auto-score: ${r.data?.autoScore ?? 0}. Resubmission is now locked.` });
      await loadTasks();
      const refreshed = await axios.get(`${API_URL}/api/studentassignments/student/task/${detail.task.AssignmentID}`, { headers:authHeaders });
      setDetail(refreshed.data);
      setMainTab(1);
    } catch (e) { setMsg({ type:"error", text:e.response?.data?.error || "Submission failed" });
    }
    finally { setLoading(false);
    }
  };


  const openSubmissionAttachment = async () => {
    const submissionID = detail?.submission?.SubmissionID;
    if (!submissionID || !detail?.submission?.HasAttachment) return;

    try {
      const r = await axios.get(
        `${API_URL}/api/studentassignments/student/submission/${submissionID}/attachment`,
        {
          headers: authHeaders,
          responseType: "blob",
        }
      );

      const blobUrl = URL.createObjectURL(r.data);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      setMsg({
        type: "error",
        text:
          e.response?.data?.error ||
          "Failed to open submitted attachment",
      });
    }
  };


  const openTaskAttachment = async () => {
    const assignmentID = detail?.task?.AssignmentID || selected?.AssignmentID || selected?.id;
    if (!assignmentID) return;
    try {
      const r = await axios.get(`${API_URL}/api/studentassignments/task/${assignmentID}/attachment`, {
        headers: authHeaders,
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(r.data);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      setMsg({ type: "error", text: e.response?.data?.error || "Failed to open attachment" });
    }
  };

  const statusColor = s => s === "GRADED" ? "success" : s === "SUBMITTED" ? "info" : "warning";
  const taskSubjects = detail ? parseJsonArray(detail.task.SubjectCodes) : [];

  return (
  <Box sx={pageWrapSx}>
  <Paper elevation={5} sx={rootPaperSx}>
  <Box sx={headerSx}>
  <Typography sx={{ fontSize: "1.1rem", fontWeight: 900 }}>
  My Assignments & Assessments
  </Typography>
  <Typography sx={{ opacity: 0.9, fontSize: ".85rem" }}>
  Complete assigned work, review submissions, and view grades and teacher remarks.
  </Typography>
  </Box>

  <Box sx={{ p: 2, bgcolor: BG }}>
  {!selected ? (
    <Paper elevation={0} sx={surfaceSx}>
    <Tabs
    value={mainTab}
    onChange={(_, v) => setMainTab(v)}
    sx={{
        px: 1,
        borderBottom: `1px solid ${BORDER}`,
        "& .MuiTab-root": { textTransform: "none", fontWeight: 800 },
        "& .Mui-selected": { color: THEME_ORANGE },
        "& .MuiTabs-indicator": { backgroundColor: THEME_ORANGE },
      }}
    >
    <Tab label="To Submit" icon={<AssignmentIcon />} iconPosition="start" />
    <Tab label="Submitted" icon={<FolderOpenIcon />} iconPosition="start" />
    <Tab label="Grade & Remarks" icon={<GradingIcon />} iconPosition="start" />
    </Tabs>

    <Box sx={{ p: 2 }}>
    <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2 }}>
    <Grid container spacing={1.5}>
    <Grid item xs={12} sm={6} md={2.4}>
    <TextField select fullWidth size="small" label="SubjectCode" value={subjectCodeFilter} onChange={(e)=>setSubjectCodeFilter(e.target.value)} sx={inputSx}>
    <MenuItem value="">All SubjectCodes</MenuItem>{subjectCodeOptions.map((x)=><MenuItem key={x} value={x}>{x}</MenuItem>)}
    </TextField>
    </Grid>
    <Grid item xs={12} sm={6} md={2.4}>
    <TextField select fullWidth size="small" label="SubjectNameCode" value={subjectNameCodeFilter} onChange={(e)=>setSubjectNameCodeFilter(e.target.value)} sx={inputSx}>
    <MenuItem value="">All SubjectNameCodes</MenuItem>{subjectNameCodeOptions.map((x)=><MenuItem key={x} value={x}>{x}</MenuItem>)}
    </TextField>
    </Grid>
    <Grid item xs={12} sm={6} md={2.4}>
    <TextField select fullWidth size="small" label="Assignment Group" value={assignmentGroupFilter} onChange={(e)=>setAssignmentGroupFilter(e.target.value)} sx={inputSx}>
    <MenuItem value="">All Groups</MenuItem>{assignmentGroupOptions.map((x)=><MenuItem key={x} value={x}>{x}</MenuItem>)}
    </TextField>
    </Grid>
    <Grid item xs={12} sm={6} md={2.4}>
    <TextField fullWidth size="small" label="Title" value={titleFilter} onChange={(e)=>setTitleFilter(e.target.value)} sx={inputSx}/>
    </Grid>
    <Grid item xs={12} sm={6} md={2.4}>
    <TextField select fullWidth size="small" label="Type" value={taskTypeFilter} onChange={(e)=>setTaskTypeFilter(e.target.value)} sx={inputSx}>
    <MenuItem value="">All</MenuItem><MenuItem value="ASSIGNMENT">Assignment</MenuItem><MenuItem value="ASSESSMENT">Assessment</MenuItem>
    </TextField>
    </Grid>
    </Grid>
    </Paper>

    <Stack spacing={1.5}>
    {tabRows.length === 0 && (
      <Alert severity="info">No items match this view.</Alert>
      )}

    {tabRows.map((t) => (
      <Paper
      key={t.AssignmentID}
      elevation={0}
      sx={{ ...surfaceSx, p: 2 }}
      >
      <Stack
      direction={{ xs: "column", md: "row" }}
      justifyContent="space-between"
      alignItems={{ md: "center" }}
      gap={1}
      >
      <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <Typography variant="h6" fontWeight={900}>{t.Title}</Typography>
      <Chip size="small" label={t.TaskType} />
      <Chip size="small" label={t.AssignmentGroupCode || "No Group"} />
      <Chip
      size="small"
      color={statusColor(t.SubmissionStatus)}
      label={t.SubmissionStatus}
      />
      </Stack>

      <Typography variant="body2" color="text.secondary" mt={0.5}>
      Subjects: {parseJsonArray(t.SubjectCodes).join(", ") || "-"}
      {" • "}Due: {t.DueDate ? new Date(t.DueDate).toLocaleString() : "-"}
      {" • "}Marks: {t.TotalMarks}
      </Typography>

      {t.SubmissionStatus === "GRADED" && (
        <Typography fontWeight={800} color={BLUE}>
        Score: {t.Score} / {t.TotalMarks}
        </Typography>
        )}
      </Box>

      <Button
      variant="contained"
      sx={primaryBtnSx}
      onClick={() => openTask(t)}
      >
      {t.SubmissionStatus === "NOT_SUBMITTED" ? "Open" : "View"}
      </Button>
      </Stack>
      </Paper>
      ))}
    </Stack>
    </Box>
    </Paper>
    ) : (
    <Stack spacing={2}>
    {loading && !detail ? (
      <CircularProgress />
      ) : (
      <>
      <Button
      sx={{ alignSelf: "flex-start" }}
      onClick={() => {
          setSelected(null);
          setDetail(null);
        }}
      >
      ← Back to dashboard
      </Button>

      {detail && (
        <>
        <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="h5" fontWeight={900}>{detail.task.Title}</Typography>
        <Chip label={detail.task.TaskType} />
        <Chip label={detail.task.AssignmentGroupCode || "No Group"} />
        {detail.submission && (
          <Chip
          color={statusColor(detail.submission.Status)}
          label={detail.submission.Status}
          />
          )}
        </Stack>

        <Typography sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
        {detail.task.Description}
        </Typography>

        <Typography variant="body2" color="text.secondary" mt={1}>
        Subjects: {taskSubjects.join(", ") || "-"}
        {" • "}Total Marks: {detail.task.TotalMarks}
        </Typography>

        {detail.task.AttachmentPath && (
          <Button
            sx={{ mt: 1 }}
            variant="outlined"
            startIcon={<AttachFileIcon />}
            onClick={openTaskAttachment}
          >
            Open Attachment{detail.task.AttachmentName ? ` - ${detail.task.AttachmentName}` : ""}
          </Button>
        )}

        {detail.task.Instructions && (
          <>
          <Divider sx={{ my: 1.5 }} />
          <Typography fontWeight={900} color={BLUE}>Instructions</Typography>
          <Typography sx={{ whiteSpace: "pre-wrap" }}>
          {detail.task.Instructions}
          </Typography>
          </>
          )}
        </Paper>

        {detail.questions.map((q, i) => {
            const a = answers[q.id] || {};
            const locked = !!detail.submission;

            return (
            <Paper key={q.id} elevation={0} sx={{ ...surfaceSx, p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
            <Typography fontWeight={900}>
            {i + 1}. {q.QuestionText}
            </Typography>
            <Chip size="small" label={`${q.Points} marks`} />
            </Stack>

            {q.QuestionType === "MCQ" ? (
              <>
              <RadioGroup
              value={a.selectedOptionIndex ?? ""}
              onChange={(e) => {
                  if (!locked) {
                    setAnswers((x) => ({
                      ...x,
                      [q.id]: {
                        ...x[q.id],
                        selectedOptionIndex: Number(e.target.value),
                      },
                    }));
                  }
                }}
              >
              {(q.options || []).map((o, oi) => (
                <FormControlLabel
                key={oi}
                value={oi}
                disabled={locked}
                control={<Radio />}
                label={o}
                />
                ))}
              </RadioGroup>

              {detail.submission?.Status === "GRADED" && (
                <Box mt={1}>
                <Typography fontWeight={800}>
                Correct answer: {q.CorrectOptionIndex == null
                  ? "-"
                  : (q.options || [])[q.CorrectOptionIndex]}
                </Typography>
                <Chip
                size="small"
                color={a.IsCorrect ? "success" : "error"}
                label={a.IsCorrect ? `Correct +${a.AutoScore || 0}` : "Incorrect"}
                />
                {q.Explanation && (
                  <Typography variant="body2" mt={1}>{q.Explanation}</Typography>
                  )}
                </Box>
                )}
              </>
              ) : (
              <>
              <TextField
              sx={{ mt: 1.5, ...inputSx }}
              fullWidth
              multiline
              minRows={4}
              label={locked ? "Your submitted answer" : "Your answer"}
              value={a.answerText || ""}
              disabled={locked}
              onChange={(e) =>
                setAnswers((x) => ({
                  ...x,
                  [q.id]: {
                    ...x[q.id],
                    answerText: e.target.value,
                  },
                }))
              }
              />

              {detail.submission?.Status === "GRADED" && (
                <Box mt={1}>
                <Chip
                size="small"
                label={`Awarded: ${a.AwardedPoints ?? 0} / ${q.Points}`}
                />
                {a.GraderFeedback && (
                  <Typography variant="body2" mt={1}>
                  Question remark: {a.GraderFeedback}
                  </Typography>
                  )}
                </Box>
                )}
              </>
              )}
            </Paper>
          );
        })}

      {detail.submission ? (
        <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
        <Typography variant="h6" fontWeight={900} color={BLUE}>
        {detail.submission.Status === "GRADED"
          ? "Grade & Teacher Remarks"
          : "Submission Locked"}
        </Typography>

        <Typography variant="body2" color="text.secondary">
        Submitted: {detail.submission.SubmittedAt
          ? new Date(detail.submission.SubmittedAt).toLocaleString()
          : "-"}. Resubmission is not allowed.
        </Typography>

                           {detail.submission.HasAttachment && (
                             <Button
                               sx={{ mt: 1.5 }}
                               variant="outlined"
                               startIcon={<AttachFileIcon />}
                               onClick={openSubmissionAttachment}
                             >
                               View Submitted Attachment
                             </Button>
                           )}

        {detail.submission.Status === "GRADED" && (
          <>
          <Typography variant="h5" fontWeight={900} mt={1}>
          {detail.submission.Score} / {detail.task.TotalMarks}
          </Typography>
          <Typography fontWeight={800} mt={1}>Teacher Remarks</Typography>
          <Typography sx={{ whiteSpace: "pre-wrap" }}>
          {detail.submission.Feedback || "No teacher remarks."}
          </Typography>
          </>
          )}
        </Paper>
        ) : detail.canSubmit ? (
        <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
        <Button
        component="label"
        variant="outlined"
        startIcon={<UploadFileIcon />}
        >
        Attach file
        <input
        hidden
        type="file"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
        </Button>

        {file && <Chip sx={{ ml: 1 }} label={file.name} />}

        <Button
        sx={{ ...primaryBtnSx, ml: 2 }}
        variant="contained"
        disabled={loading}
        onClick={submit}
        >
        Submit Final
        </Button>

        <Alert severity="warning" sx={{ mt: 1.5 }}>
        Submission is final. You cannot resubmit this assignment or assessment after submitting.
        </Alert>
        </Paper>
        ) : (
        <Alert severity="warning">
        The due date has passed. This task is closed for submission.
        </Alert>
        )}
      </>
      )}
    </>
    )}
  </Stack>
  )}
</Box>
</Paper>

<Snackbar
open={!!msg.text}
autoHideDuration={5000}
onClose={() => setMsg((m) => ({ ...m, text: "" }))}
>
<Alert severity={msg.type}>{msg.text}</Alert>
</Snackbar>
</Box>
);
}
